declare global {
    // eslint-disable-next-line
    interface Window extends WeblabsWindow {}
}

export interface WeblabsWindow {
    renderingWeblabs?: { [key: string]: string };
}

export interface Weblab {
    isT1(): boolean;
}

export const createWeblab = (weblabsWindow: WeblabsWindow, prefix: string): Weblab => {
    const weblabTreatment = (): string | undefined => weblabsWindow?.renderingWeblabs?.[prefix]?.toLowerCase();

    return {
        isT1(): boolean {
            return weblabTreatment() === 't1';
        },
    };
};

/*
 * Used for gating IN art event styling
 * @link https://weblab.amazon.com/wl/A2I_HOMEPAGE_CARD_THEMING_360311
 */
export const A2I_HOMEPAGE_CARD_THEMING = () => createWeblab(window, 'A2I_HOMEPAGE_CARD_THEMING_360311');

/*
 * Used for sampling metrics logging at a low rate
 * @link https://weblab.amazon.com/wl/ADPT_SF_LOW_RATE_METRIC_LOGGING_1256362
 */
export const ADPT_SF_LOW_RATE_METRIC_LOGGING = () => createWeblab(window, 'ADPT_SF_LOW_RATE_METRIC_LOGGING_1256362');

/*
 * Used for sampling metrics logging at a high rate
 * @link https://weblab.amazon.com/wl/ADPT_SF_HIGH_RATE_METRIC_LOGGING_1256364
 */
export const ADPT_SF_HIGH_RATE_METRIC_LOGGING = () => createWeblab(window, 'ADPT_SF_HIGH_RATE_METRIC_LOGGING_1256364');

/*
 * Use Light Ads pixel firing logic in Safeframe for COD pixel
 * @link https://weblab.amazon.com/wl/ADPT_SF_PIXEL_FIRING_1151992
 */
export const ADPT_SF_PIXEL_FIRING_1151992 = () => createWeblab(window, 'ADPT_SF_PIXEL_FIRING_1151992');

/**
 * Used to disable client-side creative sizing
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_SERVER_SIDE_CREATIVE_SIZING_1230792
 */
export const SERVER_SIDE_CREATIVE_SIZING = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_SERVER_SIDE_CREATIVE_SIZING_1230792');

/**
 * Used to toggle the usage of new MASH getObstructions API
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_OBSTRUCTIONS_1237329
 */
export const MASH_GET_OBSTRUCTIONS = () => createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_OBSTRUCTIONS_1237329');

/*
 * Use for Sizing check as RATIO or FIXED
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_SIZING_1238561
 */
export const APM_STORES_JPS_JRS_SAFEFRAME_SIZING = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_SIZING_1238561');

/**
 * Feature flag for continuous view obstruction check. This is to improve the interval to do obstruction check.
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_CONTINUOUS_OBSTRUCTION_CHECK_1246149
 */
export const CONTINUOUS_OBSTRUCTION_CHECK = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_CONTINUOUS_OBSTRUCTION_CHECK_1246149');

/**
 * Used to enable new retail tracking with the JRS test painter.
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_CLICK_TRACKING_PAINTER_TEST_1259727
 */
export const CLICK_TRACKING_PAINTER_TEST = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_CLICK_TRACKING_PAINTER_TEST_1259727');

/**
 * Add a safezone for H1 obstruction check.
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_H1_SAFEZONE_1279523
 */
export const OBSTRUCTION_SAFEZONE = () => createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_H1_SAFEZONE_1279523');

/**
 * Deduping view tracking and pixel firing for non-ADSP SF video creatives
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_DEDUPE_SF_VIDEO_PIXELS_1279569
 */
export const DEDUPE_SF_VIDEO_PIXELS = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_DEDUPE_SF_VIDEO_PIXELS_1279569');

/**
 * Used to deprecate the changeSize API
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_DEPRECATE_CHANGESIZE_1282516
 */
export const DEPRECATE_CHANGESIZE = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_DEPRECATE_CHANGESIZE_1282516');

/**
 * Used for migrating to data attribute for lightAdDetails
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_LIGHT_AD_DETAILS_DATA_ATTR_1282877
 */
export const LIGHT_AD_DETAILS_DATA_ATTR = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_LIGHT_AD_DETAILS_DATA_ATTR_1282877');

/**
 * Use btr check for determining collapse
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_ONERROR_COLLAPSE_1285300
 */
export const SAFEFRAME_ONERROR_COLLAPSE = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_ONERROR_COLLAPSE_1285300');

/**
 * Slightly different override implementation of SafeFrame's document.write
 * @link https://weblab.amazon.com/wl/APM_STORES_JPS_JRS_SAFEFRAME_DOCUMENT_WRITE_1286180
 */
export const SAFEFRAME_DOCUMENT_WRITE = () =>
    createWeblab(window, 'APM_STORES_JPS_JRS_SAFEFRAME_DOCUMENT_WRITE_1286180');
